import { useCallback, useEffect, useRef, useState } from 'react'

export type RowCarousel = {
  ref: React.RefObject<HTMLUListElement | null>
  canPrev: boolean
  canNext: boolean
  scrollPrev: () => void
  scrollNext: () => void
}

export function useRowCarousel(): RowCarousel {
  const ref = useRef<HTMLUListElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const sync = useCallback(() => {
    const el = ref.current
    if (el === null) return
    setCanPrev(el.scrollLeft > 1)
    // 1px 은 소수 폭(322.67)이 만드는 반올림 오차 몫이다 — 없으면 끝에서 화살표가 안 꺼진다.
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (el === null) return
    sync()
    el.addEventListener('scroll', sync, { passive: true })
    // 폭이 바뀌면 «더 갈 수 있는가» 도 바뀐다(카드가 늦게 들어오는 경우 포함).
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', sync)
      ro.disconnect()
    }
  }, [sync])

  const stepPx = (): number => {
    const el = ref.current
    if (el === null) return 0
    const first = el.firstElementChild
    if (first === null) return el.clientWidth
    const gap = Number.parseFloat(getComputedStyle(el).columnGap || '0')
    return first.getBoundingClientRect().width + (Number.isNaN(gap) ? 0 : gap)
  }

  const scrollPrev = () => ref.current?.scrollBy({ left: -stepPx(), behavior: 'smooth' })
  const scrollNext = () => ref.current?.scrollBy({ left: stepPx(), behavior: 'smooth' })

  return { ref, canPrev, canNext, scrollPrev, scrollNext }
}
